import {useEffect, lazy, Suspense} from 'react'
import {Outlet, useLocation} from 'react-router-dom'
import {HeaderWrapper} from './components/header'
import {RightToolbar} from '../partials/layout/RightToolbar'
import {ScrollTop} from './components/scroll-top'
import {Content} from './components/content'
import {FooterWrapper} from './components/footer'
import {Sidebar} from './components/sidebar'
import {
  DrawerMessenger,
  ActivityDrawer,
  InviteUsers,
  UpgradePlan,
  ThemeModeProvider,
} from '../partials'
import {PageDataProvider} from './core'
import {reInitMenu} from '../helpers'
import {ToolbarWrapper} from './components/toolbar'
import { RemindarSignalRHub } from './components/signalRHub/RemindarSignalRHub'
import {SpeechToSpeechProvider, useSpeechToSpeech} from '../../app/context/SpeechToSpeechContext'
import {MfaGracePeriodBanner} from '../../components/MfaGracePeriodBanner'

const SpeechToSpeechWidget = lazy(() =>
  import('../../components/SpeechToSpeech').then((m) => ({default: m.SpeechToSpeechWidget}))
)

const SpeechToSpeechWidgetWithClose = () => {
  const {isWidgetOpen, isCallActive, closeWidget} = useSpeechToSpeech()
  if (!isWidgetOpen) return null
  // Close affordances are present while configuring on the Ready view, but
  // suppressed once a call is live so it can't be accidentally killed by an
  // outside click or stray X-tap. The sidebar menu toggle still works.
  return (
    <>
      {!isCallActive && (
        <div
          className='speech-widget-backdrop'
          onClick={closeWidget}
          role='button'
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Escape' && closeWidget()}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            background: 'transparent',
          }}
          aria-label='Close widget'
        />
      )}
      <div className='speech-widget-float'>
        {!isCallActive && (
          <button
            type='button'
            onClick={closeWidget}
            aria-label='Close Speech to Speech'
            className='btn btn-icon btn-sm btn-light-danger position-absolute rounded-circle'
            style={{top: -8, right: -8, zIndex: 10000}}
          >
            <i className='bi bi-x fs-2' />
          </button>
        )}
        <Suspense fallback={<div className='speech-widget-loading' style={{position: 'fixed', bottom: 20, right: 20, zIndex: 9999, padding: 16, background: '#fff', borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.15)'}}>Loading...</div>}>
          <SpeechToSpeechWidget />
        </Suspense>
      </div>
    </>
  )
}

const MasterLayoutInner = () => {
  return (
    <>
      <div className='d-flex flex-column flex-root app-root' id='kt_app_root'>
        <div className='app-page flex-column flex-column-fluid' id='kt_app_page'>
          <HeaderWrapper />
          <div className='app-wrapper flex-column flex-row-fluid' id='kt_app_wrapper'>
            <Sidebar />
            <RemindarSignalRHub />
            <div
              className='app-main flex-column flex-row-fluid justify-content-between'
              id='kt_app_main'
            >
              <MfaGracePeriodBanner />
              <div
                className='d-flex flex-column flex-column-fluid'
                style={{minHeight: 'calc(100vh - 120px)'}}
              >
                <ToolbarWrapper />
                <Content>
                  <Outlet />
                </Content>
              </div>
              <FooterWrapper />
            </div>
          </div>
        </div>
      </div>

      {/* begin:: Drawers */}
      <ActivityDrawer />
      <RightToolbar />
      <DrawerMessenger />
      {/* end:: Drawers */}

      {/* begin:: Modals */}
      <InviteUsers />
      <UpgradePlan />
      {/* end:: Modals */}
      <SpeechToSpeechWidgetWithClose />
      <ScrollTop />
    </>
  )
}

const MasterLayout = () => {
  const location = useLocation()
  useEffect(() => {
    reInitMenu()
  }, [location.key])

  return (
    <SpeechToSpeechProvider>
      <PageDataProvider>
        <ThemeModeProvider>
          <MasterLayoutInner />
        </ThemeModeProvider>
      </PageDataProvider>
    </SpeechToSpeechProvider>
  )
}

export {MasterLayout}
